import { useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    Dialog,
    DialogActionButton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { apiQuery, useSupportsBacklinks } from '../../state';
import { BacklinksAlert, BacklinksAlertWrapper } from '../ContentReferences';

/**
 * Dialog to confirm the deletion of a file.
 */
export function DeleteFileDialog(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    file: hiveCore.RevisionFile;
    onClose: () => void;
}) {
    const { space, changeRequest, file, onClose } = props;

    const hiveBranch = useHiveBranch();

    const onDelete = async () => {
        assertIsDefined(hiveBranch);

        analytics.logEvent(
            analytics.createSpaceEvent(space, {
                type: 'edit_remove_file',
            })
        );

        await hiveBranch.change((change) => {
            change.removeFile(file.id);
        });
    };

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Delete file</Heading>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-4">
                <p className="text-secondary text-ui-base">
                    This file will be deleted. Its content will still be available in the version
                    history.
                </p>

                <WarnOfFileBacklinksWrapper
                    file={file}
                    space={space}
                    changeRequest={changeRequest}
                    onClose={onClose}
                />
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <DialogActionButton label="Delete" kind="danger" onPress={onDelete} />
            </DialogFooter>
        </Dialog>
    );
}

/** Warn the users about links to this page. */
function WarnOfFileBacklinksWrapper(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    file: hiveCore.RevisionFile;
    onClose: () => void;
}): React.ReactElement | null {
    const { space, changeRequest, file, onClose } = props;
    const supportsBacklinks = useSupportsBacklinks({ isInChangeRequest: !!changeRequest });
    if (!supportsBacklinks) {
        return null;
    }

    return (
        <BacklinksAlertWrapper contentType="file">
            <WarnOfFileBacklinks
                file={file}
                space={space}
                changeRequest={changeRequest}
                onClose={onClose}
            />
        </BacklinksAlertWrapper>
    );
}

function WarnOfFileBacklinks(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    file: hiveCore.RevisionFile;
    onClose: () => void;
}): React.ReactElement | null {
    const { space, changeRequest, file, onClose } = props;

    const fileBacklinksResults = useSuspenseQuery(
        changeRequest
            ? apiQuery(
                  'listChangeRequestFileBacklinks',
                  {
                      spaceId: space.id,
                      changeRequestId: changeRequest.id,
                      fileId: file.id,
                      limit: 20,
                  },
                  { nullOnError: true }
              )
            : apiQuery(
                  'listSpaceFileBacklinks',
                  {
                      spaceId: space.id,
                      fileId: file.id,
                      limit: 20,
                  },
                  { nullOnError: true }
              )
    );

    const contentLocations = fileBacklinksResults?.data?.items ?? [];
    if (contentLocations.length === 0) {
        return null;
    }

    return (
        <BacklinksAlert
            contentType="file"
            contentLocations={contentLocations}
            onNavigate={onClose}
        />
    );
}
